import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../../config/URL";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
  createTheme,
  IconButton,
  Menu,
  MenuItem,
  ThemeProvider,
} from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import GlobalDelete from "../../../../components/common/GlobalDelete";
import SalaryTypeAdd from "./SalaryTypeAdd";
import SalaryTypeEdit from "./SalaryTypeEdit";
import { exportVisibleColumnsToCSV } from "../../../../components/common/ExportCsv";

const SalaryType = () => {
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");

  const canRead = Boolean(storedScreens?.salarySettingRead);
  const canCreate = Boolean(storedScreens?.salarySettingCreate);
  const canUpdate = Boolean(storedScreens?.salarySettingUpdate);
  const canDelete = Boolean(storedScreens?.salarySettingDelete);

  const hideActionColumn = !canUpdate && !canDelete;

  const getData = async () => {
    try {
      const response = await api.get("/getAllSalarySetting");
      setDatas(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "id",
        header: "",
        size: 20,
        enableSorting: false,
        enableHiding: true,
        Cell: ({ cell }) =>
          hideActionColumn ? null : (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchor(e.currentTarget);
                setSelectedId(cell.getValue());
              }}
            >
              <MoreVertIcon />
            </IconButton>
          ),
      },
      {
        accessorKey: "salaryType",
        header: "Salary Type",
      },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ],
    [hideActionColumn]
  );

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data: datas,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      columnVisibility: {
        createdBy: false,
        createdAt: false,
        updatedBy: false,
        updatedAt: false,
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, datas, "SalaryType.csv")
        }
      >
        Export CSV
      </button>
    ),
  });

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: "#eb862a",
            },
          },
          track: { backgroundColor: "#e0e0e0" },
          thumb: { color: "#eb862a" },
          switchBase: {
            "&.Mui-checked": { color: "#eb862a" },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#eb862a",
            },
          },
        },
      },
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  return (
    <div className="container-fluid center">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <ol className="breadcrumb" style={{ listStyle: "none", margin: 0 }}>
          <li>
            <Link to="/" className="custom-breadcrumb">
              Home
            </Link>
            <span className="breadcrumb-separator"> &gt; </span>
          </li>
          <li>
            &nbsp;Settings<span className="breadcrumb-separator"> &gt; </span>
          </li>
          <li>
            &nbsp;HR Settings
            <span className="breadcrumb-separator"> &gt; </span>
          </li>
          <li className="breadcrumb-item active">&nbsp;Salary Type</li>
        </ol>

        {canCreate && <SalaryTypeAdd onSuccess={getData} />}
      </div>

      <div className="card">
        <div
          className="d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="dot active"></div>
            <span className="ms-2 text-muted">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Salary Type
              </span>
            </span>
          </div>
        </div>

        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>
            {!hideActionColumn && (
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                {canUpdate && (
                  <MenuItem>
                    <SalaryTypeEdit
                      id={selectedId}
                      onSuccess={getData}
                      handleMenuClose={handleMenuClose}
                    />
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem>
                    <GlobalDelete
                      path={`/deleteSalarySetting/${selectedId}`}
                      onDeleteSuccess={getData}
                      onOpen={handleMenuClose}
                    />
                  </MenuItem>
                )}
              </Menu>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SalaryType;
